import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, Subject, distinctUntilChanged, takeUntil } from 'rxjs';
import { IProfileDetails } from 'src/app/core/domain/profile.model';
import { getFormsList } from 'src/app/core/state/global';
import { getProfileData } from 'src/app/core/state/profile';
import { createSlug } from '../../utils/common-methods';
import { FormManagementService } from 'src/app/core/service/form-management.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastAlertService } from '../../utils/toast.service';
import { CommonService } from 'src/app/core/service/common.service';

@Component({
  selector: 'app-form-next-step-button',
  templateUrl: './form-next-step-button.component.html',
  styleUrls: ['./form-next-step-button.component.scss']
})
export class FormNextStepButtonComponent implements OnInit, OnDestroy {

  @Input() url: string;
  @Input() disableNavigation = false;
  @Input() disableNavigationMessage = ' Please fill in all the required information.';

  // SAN-500 — set only at the live embed site inside each of the 9 primary-
  // step components (Finding 1's list); every other embed site across the
  // app defaults to false, so this component keeps rendering Next Step for
  // them exactly as before. When true, the Next-Step button is replaced by a
  // Submit button, unconditionally (not gated by buttonVisible/getNextRoute
  // — decisions 3 + 5), whose disabled state is driven solely by
  // `submitDisabled` (the type's whole-profile completeness threshold, a
  // different signal from `disableNavigation`, which reflects only the
  // current step's own form validity).
  @Input() isLastPrimaryStep = false;
  @Input() submitDisabled = false;
  // SAN-516 follow-up — once the profile has already been submitted for
  // approval (or approved), the Submit button should disappear entirely
  // rather than just render disabled/greyed-out (confirmed with the user,
  // app-wide for every primary step + custom-form tab, not just this one).
  @Input() alreadySubmitted = false;

  @Output() next = new EventEmitter()
  // SAN-500 — emitted on click when isLastPrimaryStep is true; this
  // component does not know about per-type Swal copy, dispatch, or
  // navigation — the embedding primary-step component's own handler does
  // that work in response to this event.
  @Output() submit = new EventEmitter<void>()

  private destroyed$: Subject<void> = new Subject();
  public getProfileData$: Observable<IProfileDetails>;
  profileData: IProfileDetails;

  buttonVisible = true;
  previousButtonVisible = true;
  isPreviewPage = false;

  constructor(
    private store: Store,
    private router: Router,
    private toastAlertService: ToastAlertService,
    private commonService: CommonService,
    private route: ActivatedRoute,
  ) {
    this.isPreviewPage = this.route.snapshot.data?.['preview']
  }

  async ngOnInit() {

    this.getProfileData$ = this.store.pipe(select(getProfileData));

    this.getProfileData$.pipe(distinctUntilChanged(), takeUntil(this.destroyed$)).subscribe((res) => {
      if (res) {
        this.profileData = res;

        this.store.pipe(select(getFormsList)).pipe(distinctUntilChanged(), takeUntil(this.destroyed$)).subscribe(async (res) => {

          this.commonService.editPageLinks$.subscribe(async (res)=>{
            if(res) {
              this.buttonVisible = !!(await this.commonService.getNextRoute())
              this.previousButtonVisible = !!(await this.commonService.getPreviousRoute())
            }
          })
          // if (!res[this.profileData.accountType]?.length && this.url.includes('/first')) {
          //   this.buttonVisible = false
          // } else {
          //   this.buttonVisible = true
          // }
        })
      }
    })
  }

  handleClick() {
    if (!this.disableNavigation || this.isPreviewPage) {
      this.next.emit()
      // this.router.navigate([this.url]);
      this.commonService.goToNextPage()

    } else if (this.disableNavigationMessage) {
      this.toastAlertService.showToast(this.disableNavigationMessage, 'error')
    }
  }

  // SAN-500 — Submit is purely click-triggered: emit and let the host
  // primary-step component do its own dispatch/confirm-modal/navigate. No
  // navigation happens here (unlike handleClick()/handlePreviousClick()).
  handleSubmitClick() {
    this.submit.emit();
  }

  // Fix (2026-08-24): Previous must never be blocked by the CURRENT page's
  // own validity -- disableNavigation reflects whether the page you're
  // LEAVING is complete, which is only relevant when moving forward. Going
  // back to an already-visited page must always be allowed, same principle
  // as SAN-498's tab-guard (backward navigation is never blocked).
  handlePreviousClick() {
    this.commonService.goToPreviousPage()
  }


  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
